import * as React from "react";
import { tv, VariantProps } from "tailwind-variants";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { TitleProps, Title } from "./Title";
import { SimpleRichTextProps, SimpleRichText } from "./SimpleRichText";
import { ImageWithAspectRatio } from "./ImageWithAspectRatio";
import {
  IconProps,
  ImageWithAspectRatioProps,
  CardBackgroundColorType,
} from "./types";

export const cardStats = tv({
  base: cn(
    "uipm:flex uipm:h-full uipm:items-start uipm:gap-6",
    "uipm:font-normal",
    "uipm:rounded-none uipm:md:rounded-[20px]"
  ),
  variants: {
    backgroundColor: {
      primary:
        "uipm:bg-[var(--surface-surface-accent)] uipm:text-[var(--typography-colors-text-inverted)]",
      secondary:
        "uipm:bg-[var(--surface-surface-accent-secondary)] uipm:text-[var(--typography-colors-text-heading)]",
      "secondary-light":
        "uipm:bg-[var(--surface-surface-accent-light)] uipm:text-[var(--typography-colors-text-heading)]",
      light:
        "uipm:bg-[var(--surface-surface-secondary)] uipm:text-[var(--typography-colors-text-heading)]",
      "very-light":
        "uipm:bg-[#F7F7F7] uipm:text-[var(--typography-colors-text-heading)]",
      white:
        "uipm:bg-[var(--surface-surface-primary)] uipm:text-[var(--typography-colors-text-heading)]",
      none: "uipm:bg-transparent uipm:text-[var(--typography-colors-text-heading)]",
    },
    verticalPadding: {
      default: "uipm:py-8",
      small: "uipm:py-[var(--container-card-padding)]",
      medium: "uipm:py-[var(--container-card-medium-padding)]",
      large: "uipm:py-[var(--container-card-large-padding)]",
    },
    horizontalPadding: {
      default: "uipm:px-8",
      small: "uipm:px-[var(--container-card-padding)]",
      medium: "uipm:px-[var(--container-card-medium-padding)]",
      large: "uipm:px-[var(--container-card-large-padding)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
    verticalPadding: "default",
    horizontalPadding: "default",
  },
});

const textWrapper = tv({
  base: "uipm:font-normal uipm:-mt-1",
  variants: {
    backgroundColor: {
      primary: "uipm:text-[var(--typography-colors-text-inverted-faded)]",
      secondary: "uipm:text-[var(--typography-colors-text-accent)]",
      "secondary-light": "uipm:text-[var(--typography-colors-text-accent)]",
      light: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      "very-light": "uipm:text-[var(--typography-colors-text-faded-dark)]",
      white: "uipm:text-[var(--typography-colors-text-faded-dark)]",
      none: "uipm:text-[var(--typography-colors-text-body)]",
    },
  },
  defaultVariants: {
    backgroundColor: "primary",
  },
});

export interface CardStatsProps extends Omit<ComponentProps<"div">, "title"> {
  title?: TitleProps | null;
  text?: SimpleRichTextProps | null;
  icon?: ImageWithAspectRatioProps | null;
  backgroundColor?: CardBackgroundColorType;
  verticalPadding?: "default" | "small" | "medium" | "large";
  horizontalPadding?: "default" | "small" | "medium" | "large";
}

export const CardStats: React.FC<CardStatsProps> = ({
  title,
  text,
  icon,
  backgroundColor = "primary",
  verticalPadding = "default",
  horizontalPadding = "default",
  className,
  ...props
}) => {
  return (
    <div
      className={cn(
        cardStats({ backgroundColor, verticalPadding, horizontalPadding }),
        className
      )}
      {...props}
    >
      {icon && (
        <div className="uipm:w-16 uipm:h-16 uipm:flex-shrink-0">
          <ImageWithAspectRatio {...icon} sizes="64px" />
        </div>
      )}
      <div className="uipm:flex uipm:flex-col uipm:flex-1 uipm:font-normal">
        {title && <Title {...title} className={title.className} />}
        {text && (
          <div className={textWrapper({ backgroundColor })}>
            <SimpleRichText {...text} />
          </div>
        )}
      </div>
    </div>
  );
};

export type CardStatsVariants = VariantProps<typeof cardStats>;
